<script lang="ts">
/**
 * Fixed bottom footer bar with automatic layout coordination.
 *
 * When placed inside an `AppLayout`, automatically measures its own height
 * via `ResizeObserver` and registers it with the layout state. Sibling
 * `AppPagePanel` components read this height and adjust their body padding
 * so content is never hidden behind the footer.
 *
 * Handles safe-area insets for notched devices (iPhone home indicator).
 * Supports show/hide via `v-model` with animated transitions.
 *
 * @example
 * ```vue
 * <AppFooter bordered class="lg:hidden">
 *   <SbTabs v-model="tab" stretch>
 *     <SbRouteTab name="home" icon="i-lucide-house" label="Home" to="/" exact />
 *     <SbRouteTab name="items" icon="i-lucide-package" label="Items" to="/items" />
 *   </SbTabs>
 * </AppFooter>
 * ```
 */
export interface AppFooterProps {
  /** Show or hide the footer. When hidden, reports height as 0 to the layout. @defaultValue `true` */
  modelValue?: boolean
  /** Add a top border. @defaultValue `false` */
  bordered?: boolean
  /** Add a drop shadow above the footer. @defaultValue `false` */
  elevated?: boolean
  /** HTML tag to render as. @defaultValue `"footer"` */
  as?: string
}
</script>

<script setup lang="ts">
import { computed, ref, watch, inject, onMounted, onBeforeUnmount } from "vue"

const props = withDefaults(defineProps<AppFooterProps>(), {
  modelValue: true,
  bordered: false,
  elevated: false,
  as: "footer",
})

defineEmits<{
  "update:modelValue": [value: boolean]
}>()

const layout = inject<{
  registerFooter: (height: number) => void
} | null>("app-layout", null)

const footerEl = ref<HTMLElement | null>(null)
let observer: ResizeObserver | null = null

function reportHeight(height: number) {
  layout?.registerFooter(height)
  document.documentElement.style.setProperty("--app-footer-height", `${height}px`)
}

onMounted(() => {
  if (!footerEl.value) return
  reportHeight(footerEl.value.getBoundingClientRect().height)
  observer = new ResizeObserver(([entry]) => {
    reportHeight(entry.target.getBoundingClientRect().height)
  })
  observer.observe(footerEl.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  reportHeight(0)
})

watch(
  () => props.modelValue,
  (visible) => {
    if (!visible) reportHeight(0)
  },
)

const classes = computed(() => [
  "app-footer",
  "fixed bottom-0 inset-x-0 z-50",
  "bg-default",
  props.bordered && "border-t border-default",
  props.elevated && "shadow-[0_-1px_3px_0_rgba(0,0,0,0.1)]",
  !props.modelValue && "translate-y-full",
  "transition-transform duration-200",
])
</script>

<template>
  <component
    :is="as"
    v-if="modelValue"
    ref="footerEl"
    :class="classes"
    class="pb-[max(0.5rem,var(--safe-bottom))]"
  >
    <slot />
  </component>
</template>
